import { JSX, useEffect, useRef, useState } from 'react';
import { StyleguideComponentProps } from 'lib/component-props';
import {
  SitecoreContextValue,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Icons from 'components/atoms/Icons';

type BreadcrumbProps = StyleguideComponentProps & {
  fields: {
    url: string;
    id: string;
    items: Array<{
      url: string;
      id: string;
      displayName: string;
      fields: {
        breadcrumbtitle: { value: string };
      };
    }>;
  };
};

interface CustomSitecoreContextValue {
  sitecoreContext: SitecoreContextValue;
}

const relativePathStartsWith = (path: unknown, value: string): boolean => {
  return typeof path === 'string' && path.startsWith(`${value}`);
};

const Breadcrumb = (props: BreadcrumbProps): JSX.Element => {
  const context: CustomSitecoreContextValue = useSitecoreContext({
    updatable: false,
  });

  const [isEllipsisOpen, setIsEllipsisOpen] = useState(false);
  const ellipsisRef = useRef<HTMLLIElement | null>(null);

  if (!props.fields || !props.fields.items || props.fields.items.length === 0) {
    return <p>No datasource set</p>;
  }

  let breadcrumbStack = props.fields.items;

  const urlPath = context?.sitecoreContext
    ? context.sitecoreContext.itemPath
    : '';

  let isSistersHistory = false;

  if (
    relativePathStartsWith(urlPath, '/sisters-history')
  ) {
    isSistersHistory = true;
  }

  if (
    relativePathStartsWith(urlPath, '/care') ||
    relativePathStartsWith(urlPath, '/r2') ||
    relativePathStartsWith(urlPath, '/gic')
  ) {
    breadcrumbStack = breadcrumbStack.slice(1);
  }
  if (!breadcrumbStack || breadcrumbStack.length === 0) {
    return <p>No datasource set</p>;
  }

  const lastLink = breadcrumbStack[breadcrumbStack.length - 1];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        ellipsisRef.current &&
        !ellipsisRef.current.contains(event.target as Node)
      ) {
        setIsEllipsisOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderCrumb = (
    link: (typeof breadcrumbStack)[number],
    hideIcon: boolean = false
  ) => {
    const isLast = lastLink.id === link.id;

    if (!isLast) {
      return (
        <a href={link.url}>
          {link.fields.breadcrumbtitle.value}
          {!hideIcon && <Icons id="chevron-next" />}
        </a>
      );
    }
    return link.fields.breadcrumbtitle.value;
  };

  const hasManyItems = breadcrumbStack.length > 4;

  return (
    <div className="overhauled" id={`breadcrumb-${props.rendering?.uid}`}>
      <div className={`breadcrumb${isSistersHistory ? ' sisters-breadcrumb' : ''}`}>
        <div className="breadcrumb-inner jss-inner-container">
          <ul>
            {!hasManyItems && (
              <>
                {breadcrumbStack.map(link => (
                  <li key={link.id}>{renderCrumb(link)}</li>
                ))}
              </>
            )}

            {hasManyItems && (
              <>
                <li key={breadcrumbStack[0].id}>
                  {renderCrumb(breadcrumbStack[0])}
                </li>
                <li className="breadcrumb-ellipsis" ref={ellipsisRef}>
                  <button
                    type="button"
                    className="breadcrumb-ellipsis-trigger"
                    onClick={() => setIsEllipsisOpen(prev => !prev)}
                    aria-haspopup="true"
                    aria-expanded={isEllipsisOpen}
                    aria-label="Breadcrumb Ellipsis"
                  >
                    ...
                    <Icons id="chevron-next" />
                  </button>

                  {isEllipsisOpen && (
                    <div className="breadcrumb-ellipsis-menu">
                      <ul>
                        {breadcrumbStack
                          .slice(1, breadcrumbStack.length - 2)
                          .map(link => (
                            <li key={link.id}>{renderCrumb(link, true)}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </li>
                {breadcrumbStack.slice(-2).map(link => (
                  <li key={link.id}>{renderCrumb(link)}</li>
                ))}
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Breadcrumb;
